import { useState } from 'react';
import { RefreshCw, Shield, Users, ScrollText, Plus, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import { api } from '../services/api.js';
import { useApi } from '../hooks/useApi.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { formatDate } from '../lib/utils.js';

export default function Admin() {
  const toast = useToast();
  const { data: stats,    reload: reloadStats  } = useApi(() => api.adminStats(),    []);
  const { data: users,    reload: reloadUsers  } = useApi(() => api.adminUsers(),    []);
  const { data: logs,     reload: reloadLogs   } = useApi(() => api.adminLogs(100),  []);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });


  const counts = stats?.data?.counts || {};
  const recent = stats?.data?.recentLogs || [];
  const userItems = users?.data?.items || [];
  const logItems  = logs?.data?.items  || [];

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) return;
    try {
      await api.adminCreateUser(newUser);
      setNewUser({ username: '', password: '', role: 'user' });
      toast.success('User created');
      reloadUsers();
    } catch (err) { toast.error(err.message); }
  };

  const deleteUser = async (id) => {
    if (!confirm('Delete this user and all of their data?')) return;
    try { await api.adminDeleteUser(id); toast.info('User deleted'); reloadUsers(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <PageHeader
        title="Admin"
        subtitle="Users, storage, and devices"
        actions={
          <button className="btn-ghost" onClick={() => { reloadStats(); reloadUsers(); reloadLogs(); }}><RefreshCw size={14} /> Refresh</button>
        }
      />

      <section className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Users"   value={counts.users ?? '-'} />
        <Stat label="Clips"   value={counts.clips ?? '-'} />
        <Stat label="Snaps"   value={counts.snaps ?? '-'} />
        <Stat label="Archived" value={counts.archivedSnaps ?? '-'} />
        <Stat label="Devices" value={counts.devices ?? '-'} />
      </section>

      <section className="card mb-4">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase text-zinc-400"><Users size={14} /> Users</h2>
        <form onSubmit={createUser} className="mb-3 flex flex-col gap-2 sm:flex-row">
          <input className="input flex-1" placeholder="username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
          <input className="input flex-1" type="password" placeholder="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
          <select className="input sm:w-32" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn-primary" type="submit"><Plus size={14} /> Add</button>
        </form>
        <ul className="space-y-1">
          {userItems.map((u) => (
            <li key={u.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-zinc-900/40 px-3 py-2 text-sm">
              <div>
                <div className="font-medium">{u.username}</div>
                <div className="text-xs text-zinc-500">created {formatDate(u.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="chip">{u.role}</span>
                <button className="btn-danger !px-2 !py-1" onClick={() => deleteUser(u.id)}><Trash2 size={12} /></button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="card mb-4">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase text-zinc-400"><Shield size={14} /> Recent activity</h2>
        <ul className="space-y-1">
          {(recent.length ? recent : logItems.slice(0, 20)).map((l) => (
            <li key={l.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-zinc-900/40 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1 truncate">
                <span className="chip mr-2">{l.event_type}</span>
                <span className="text-zinc-300">{l.description || '-'}</span>
              </div>
              <div className="ml-2 text-xs text-zinc-500">{formatDate(l.created_at)}</div>
            </li>
          ))}
          {recent.length === 0 && logItems.length === 0 && <li className="text-sm text-zinc-500">No activity yet.</li>}
        </ul>
      </section>

      <section className="card">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase text-zinc-400"><ScrollText size={14} /> Audit log (latest {logItems.length})</h2>
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-zinc-500">
              <tr><th className="py-1">When</th><th>User</th><th>Event</th><th>Description</th></tr>
            </thead>
            <tbody>
              {logItems.map((l) => (
                <tr key={l.id} className="border-t border-border/60">
                  <td className="py-1 text-zinc-500">{formatDate(l.created_at)}</td>
                  <td className="text-zinc-400">{l.user_id ?? '-'}</td>
                  <td><span className="chip">{l.event_type}</span></td>
                  <td className="truncate text-zinc-300">{l.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="card">
    <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
  </div>
);
